import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { IconComponent } from '@shared/components/icon';

import type { IconName } from '@shared/components/icon';

/** Sens de tri demande. Le consommateur le traduit pour son API. */
export type SensTri = 'asc' | 'desc';

/** Intention de tri emise au clic. */
export interface IntentionTri {
  readonly champ: string;
  readonly sens: SensTri;
}

/**
 * En-tete de colonne triable pour `app-table`.
 *
 * `app-table` ne gere PAS le tri d'en-tete : le kit reserve explicitement
 * `sortable?: boolean` pour un « deferred header-sort seam ». Il expose en
 * revanche `headerTemplate`, decrit comme « interactive header content » — c'est
 * ce point d'accroche qu'on utilise, plutot que de diverger du kit.
 *
 * A poser dans un `ng-template` passe en `headerTemplate`, la colonne etant
 * declaree `headerHidden: true` pour que `header` reste le libelle accessible :
 *
 * ```html
 * <ng-template #enteteNom>
 *   <app-entete-triable
 *     libelle="Evènement" champ="nom"
 *     [champActif]="champTri()" [sens]="sensTri()"
 *     (triChange)="trier($event)" />
 * </ng-template>
 * ```
 *
 * Le composant n'ORDONNE rien : il emet l'intention, le consommateur recharge.
 * C'est la seule forme possible ici, le tri etant fait par le serveur.
 */
@Component({
  selector: 'app-entete-triable',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  /* `uppercase` et la typo sont repetes sur le <button> : la feuille de l'agent
     utilisateur impose `text-transform: none` aux controles de formulaire, et
     l'heritage ne surmonte pas cela (le Preflight de Tailwind ne remet que la
     police, la taille, la graisse et la couleur). Sans ces classes, un en-tete
     triable s'affiche en casse normale a cote d'en-tetes en majuscules.

     Aucune classe de focus : `tailwind.css` applique deja un contour a
     `button:focus-visible` pour toute l'application. */
  template: `
    <button
      type="button"
      class="group inline-flex items-center gap-1 rounded text-[11.5px] font-semibold uppercase tracking-wide text-content-tertiary hover:text-content-secondary"
      [attr.aria-label]="libelleAccessible()"
      (click)="basculer()"
    >
      <span aria-hidden="true">{{ libelle() }}</span>
      <!-- L'icone est enveloppee dans un span : app-icon pilote lui-meme la
           liaison de classe de son hote, une classe posee dessus serait ecrasee. -->
      <span
        class="inline-flex transition-opacity"
        [class.opacity-0]="!estActif()"
        [class.group-hover:opacity-40]="!estActif()"
        aria-hidden="true"
      >
        <app-icon [name]="icone()" size="xs" />
      </span>
    </button>
  `,
})
export class EnteteTriableComponent {
  readonly libelle = input.required<string>();
  /** Cle envoyee a l'API comme champ d'ordonnancement. */
  readonly champ = input.required<string>();
  /** Champ actuellement trie, tous en-tetes confondus. */
  readonly champActif = input<string | null>(null);
  readonly sens = input<SensTri>('asc');

  readonly triChange = output<IntentionTri>();

  protected readonly estActif = computed(() => this.champActif() === this.champ());

  /* Fleche reservee meme inactive (`opacity-0` et non `hidden`) : sans cela la
     largeur de la colonne sautait au changement de tri. */
  protected readonly icone = computed<IconName>(() =>
    this.estActif() && this.sens() === 'desc' ? 'arrow-down' : 'arrow-up',
  );

  /* Le libelle visible est `aria-hidden` : le bouton porte lui-meme un nom
     accessible explicite, sinon le lecteur d'ecran annoncait deux fois le titre
     (le `sr-only` pose par `headerHidden`, puis le texte du bouton). */
  protected readonly libelleAccessible = computed(() =>
    this.estActif()
      ? `${this.libelle()} — trier par ordre ${this.sens() === 'asc' ? 'décroissant' : 'croissant'}`
      : `${this.libelle()} — trier`,
  );

  protected basculer(): void {
    const sens: SensTri = this.estActif() && this.sens() === 'asc' ? 'desc' : 'asc';
    this.triChange.emit({ champ: this.champ(), sens });
  }
}
